import { useState, useEffect, useCallback } from 'react'
import { getShops, type ShopSummary, type FeaturedShopCreateData } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

interface FeaturedShopCreateModalProps {
  onClose: () => void
  onCreate: (data: FeaturedShopCreateData) => Promise<void>
}

function ShopAvatar({ name }: { name: string }) {
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#503864] text-sm font-semibold text-white">
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  )
}

export function FeaturedShopCreateModal({ onClose, onCreate }: FeaturedShopCreateModalProps) {
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  const [results, setResults] = useState<ShopSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedShop, setSelectedShop] = useState<ShopSummary | null>(null)

  const [note, setNote] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadShops = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      const response = await getShops({ search: debouncedSearch, page: 1, page_size: 10 })
      if (response.ok && response.data) {
        setResults(response.data.items)
      } else {
        setError(response.error?.message || 'Failed to search shops')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setSearching(false)
    }
  }, [debouncedSearch])

  useEffect(() => {
    loadShops()
  }, [loadShops])

  const handleSubmit = async () => {
    if (!selectedShop) {
      setError('Select a shop to feature')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await onCreate({
        shop_id: selectedShop.id,
        note: note.trim() || undefined,
        is_active: isActive,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to feature shop')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-white">Feature a Shop</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="space-y-4">
          {selectedShop ? (
            <div className="flex items-center gap-3 rounded-xl border border-[#503864] dark:border-[#7f4f9a] bg-gray-50 dark:bg-[#2d2236] p-3">
              <ShopAvatar name={selectedShop.name} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900 dark:text-white">{selectedShop.name}</div>
                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {selectedShop.slug_token ? `@${selectedShop.slug_token}` : selectedShop.owner_email}
                </div>
              </div>
              <button
                onClick={() => setSelectedShop(null)}
                className="text-xs text-[#7f4f9a] hover:text-[#503864] dark:text-[#b58fcc]"
              >
                Change
              </button>
            </div>
          ) : (
            <div>
              <CustomTextInput
                label="Shop"
                placeholder="Search shops by name or owner..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-[#3d2c4f]">
                {searching ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">Searching...</div>
                ) : results.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchInput.trim() ? 'No shops found' : 'Type to search shops'}
                  </div>
                ) : (
                  results.map((shop) => (
                    <button
                      key={shop.id}
                      onClick={() => setSelectedShop(shop)}
                      className="flex w-full items-center gap-3 border-b border-gray-100 dark:border-[#3d2c4f] px-3 py-2 text-left last:border-b-0 hover:bg-gray-50 dark:hover:bg-[#2d2236]"
                    >
                      <ShopAvatar name={shop.name} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">{shop.name}</div>
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {shop.slug_token ? `@${shop.slug_token}` : shop.owner_email}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why is this shop featured?"
              rows={3}
              className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-2 focus:ring-[#503864]/20"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
            />
            Active
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <CustomButton variant="secondary" onClick={onClose}>Cancel</CustomButton>
          <CustomButton variant="primary" onClick={handleSubmit} loading={loading} disabled={!selectedShop}>
            Feature Shop
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
